import {
    Alert,
    Box,
    Chip,
    Stack,
    Typography
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import React from 'react';
import styled from 'styled-components';
import moneyFormat from '../../../../hooks/moneyFormat';
import applyPlaceholders from './applyPlaceholders';

const Step4Review = ({ formValues }) => {

    const description = applyPlaceholders(formValues.description, formValues);
    const termsConditions = applyPlaceholders(formValues.terms_conditions, formValues);
    const specialClauses = applyPlaceholders(formValues.special_clauses ?? '', formValues);

    return (
        <Wrapper>

            {/* HEADER */}
            <Header>
                <Typography className="title">
                    Revisión final
                </Typography>

                <Typography className="subtitle">
                    Verifica toda la información antes de crear el contrato
                </Typography>
            </Header>

            {/* ALERT */}
            <Alert sx={alertStyles}>
                El contrato se creará en estado <strong>Pendiente de firmas</strong>.
                Ambas partes podrán firmarlo posteriormente.
            </Alert>

            {/* RESUMEN */}
            <SectionCard>
                <SectionTitle>
                    <PaidOutlinedIcon />
                    <Typography>Resumen comercial</Typography>
                </SectionTitle>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Tipo: ${formValues.agreement_type ?? '—'}`} />

                    {formValues.moves_amount > 0 ? (
                        <>
                            <Chip color="primary" label={`Jugadas: ${formValues.moves_amount}`} />
                            <Chip
                                variant="outlined"
                                label={`Valor: ${moneyFormat(Number(formValues.agreement_amount || 0))} ${formValues.currency ?? ''}`}
                            />
                            <Chip color="success" label={`Regalo: ${formValues.gift_moves ?? 0}`} />
                            <Chip label={`Pago: ${formValues.payment_type_desc ?? '—'}`} />
                        </>
                    ) : (
                        <Chip color="warning" label="Sin jugadas" />
                    )}
                </Stack>
            </SectionCard>

            {/* PARTES */}
            <SectionCard>
                <SectionTitle>
                    <BusinessOutlinedIcon />
                    <Typography>Partes</Typography>
                </SectionTitle>

                <PartiesGrid>
                    <PartyBox>
                        <span className="label">Vendedor</span>
                        <strong>{formValues.fbc_name}</strong>
                        <p>{formValues.fbc_address}</p>
                        <p>{formValues.fbc_email} · {formValues.fbc_phone}</p>
                    </PartyBox>

                    <PartyBox>
                        <span className="label">Comprador</span>
                        <strong>{formValues.company_name}</strong>
                        <p>{formValues.company_address}</p>
                        <p>{formValues.company_email} · {formValues.company_phone}</p>
                        <small>
                            {formValues.signatory_name} ({formValues.signatory_position})
                        </small>
                    </PartyBox>
                </PartiesGrid>
            </SectionCard>

            {/* DOCUMENTO */}
            <SectionCard>
                <SectionTitle>
                    <DescriptionOutlinedIcon />
                    <Typography>Vista previa del contrato</Typography>
                </SectionTitle>

                <DocumentPreview>
                    <h2>{formValues.title}</h2>

                    <div dangerouslySetInnerHTML={{ __html: description }} />
                    <div dangerouslySetInnerHTML={{ __html: termsConditions }} />
                    {specialClauses && (
                        <div dangerouslySetInnerHTML={{ __html: specialClauses }} />
                    )}
                </DocumentPreview>
            </SectionCard>

        </Wrapper>
    );
};

export default Step4Review;

const Wrapper = styled(Box)`
    width: 100%;
`;

const Header = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.9rem;
        margin-top: 0.2rem;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.8rem;

    svg {
        color: #d6aa3a;
    }

    p {
        font-weight: 900;
        color: #10285f;
    }
`;

const PartiesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const PartyBox = styled.div`
    padding: 0.8rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.05);
    border: 1px solid rgba(15, 34, 77, 0.1);

    .label {
        font-size: 0.7rem;
        text-transform: uppercase;
        color: rgba(15, 34, 77, 0.6);
        font-weight: 800;
    }

    strong {
        display: block;
        color: #10285f;
        margin-top: 0.2rem;
    }

    p {
        margin: 2px 0;
        font-size: 0.85rem;
        color: rgba(15, 34, 77, 0.7);
    }

    small {
        display: block;
        margin-top: 0.3rem;
        color: rgba(15, 34, 77, 0.55);
    }
`;

const DocumentPreview = styled.div`
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(15, 34, 77, 0.12);
    padding: 1.2rem;
    max-height: 400px;
    overflow-y: auto;

    h2 {
        text-align: center;
        color: #10285f;
        margin-bottom: 1rem;
        font-size: 1.1rem;
    }

    div {
        font-size: 0.9rem;
        color: #10285f;
        line-height: 1.5;
        margin-bottom: 1rem;
    }
`;

const alertStyles = {
    borderRadius: '12px',
    background: 'rgba(214,170,58,0.15)',
    color: '#10285f',
    border: '1px solid rgba(214,170,58,0.3)',
    marginBottom: '1rem'
};